import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OTHER = 'Other';

const STEPS = [
  { key: 'age', label: 'How old are you?', type: 'number' },
  {
    key: 'gender',
    label: 'Are you male or female?',
    type: 'radio',
    options: ['Female', 'Male'],
  },
  { key: 'city', label: 'What city do you live in?', type: 'text' },
  {
    key: 'spanking_hardness',
    label: 'How hard should your punishment spanking be?',
    type: 'radio_other',
    options: [
      'Not hard, it should be light and playful.',
      'A mix of light and moderate swats.',
      'Hard enough to just sting.',
      'Hard and should leave marks for several days.',
      'Hard and I should be bawling by the end of it.',
    ],
  },
  {
    key: 'how_bared',
    label: 'How do you imagine your bottom being bared?',
    type: 'radio_other',
    options: [
      "I don't wish to be spanked on my bare bottom.",
      'Ordered to strip nude.',
      'Standing in front of my disciplinarian and he lowers my pants and panties before placing me across his knee.',
      'Ordered to pull my pants down, then once over his knee he yanks my panties down.',
      'Once over his knee, he pulls my pants and panties down.',
    ],
  },
  {
    key: 'position',
    label: 'What position should you be punished in?',
    type: 'radio_other',
    options: [
      'Over the knee.',
      'Bent over something.',
      'Standing, touching my toes.',
      'Wheelbarrow.',
      'Diaper position.',
      'Lying flat on my stomach.',
    ],
  },
  {
    key: 'implement',
    label: 'What implement do you fantasize being used on you the most?',
    type: 'radio_other',
    options: [
      'Hand',
      'Hairbrush',
      'Wooden spoon',
      'Belt',
      'Whip',
      'Paddle',
      'Cane',
    ],
  },
  {
    key: 'most_important',
    label: "What's most important to you?",
    type: 'radio_other',
    options: [
      'The submission',
      'The pain',
      'The anticipation',
      'The emotional release',
      'The overall experience',
    ],
  },
  {
    key: 'aftercare',
    label: 'How does aftercare look in your eyes?',
    type: 'radio',
    options: [
      'No aftercare — I should be spanked and sent home.',
      'Massaging my bottom with moisturizer.',
      'Massaging my bottom with light fingering.',
      'Massaging my bottom with moisturizer then intercourse.',
      'Massaging my bottom with moisturizer and preparing me for anal.',
      'Massaging my bottom with moisturizer then pleasing me orally.',
      'I should be made to service my disciplinarian orally.',
      'A full body sensual massage.',
    ],
  },
  {
    key: 'safe_word',
    label: 'Do you want a safe word?',
    type: 'radio',
    options: ['Yes', 'No'],
  },
  {
    key: 'interested_in_session',
    label: 'Are you interested in discussing a potential session?',
    type: 'radio_conditional_email',
    options: ['Yes', 'No'],
  },
];

const initialAnswers = {
  age: '',
  gender: '',
  city: '',
  spanking_hardness: '',
  how_bared: '',
  position: '',
  implement: '',
  most_important: '',
  aftercare: '',
  safe_word: '',
  interested_in_session: '',
  contact_email: '',
  contact_pref: '', // 'email' or 'later'
};

const initialOthers = {
  spanking_hardness: '',
  how_bared: '',
  position: '',
  implement: '',
  most_important: '',
};

const Survey = () => {
  const { toast } = useToast();
  usePageMeta({
    title: 'Confidential Survey',
    description: 'A short confidential survey for RealOTK clients. Anonymous. Takes less than 2 minutes.',
    path: '/survey',
  });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const [others, setOthers] = useState(initialOthers);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Abandonment tracking: send partial data if user leaves without submitting
  const sessionIdRef = useRef(null);
  const submittedRef = useRef(false);
  const latestPayloadRef = useRef(null);

  if (!sessionIdRef.current) {
    sessionIdRef.current =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  // Keep latest payload in a ref so beforeunload sees current data
  useEffect(() => {
    submittedRef.current = submitted;
  }, [submitted]);

  useEffect(() => {
    const buildFinal = (key) => {
      const v = answers[key];
      if (v === OTHER) return `Other: ${(others[key] || '').trim()}`;
      return v || null;
    };
    let contactPreferenceLabel = null;
    if (answers.contact_pref === 'email') {
      contactPreferenceLabel = 'Provided email';
    } else if (answers.contact_pref === 'later') {
      contactPreferenceLabel = 'Will reach out in the future';
    }

    latestPayloadRef.current = {
      session_id: sessionIdRef.current,
      furthest_step: step + 1,
      age: parseInt(answers.age, 10) || null,
      gender: answers.gender || null,
      city: (answers.city || '').trim() || null,
      spanking_hardness: buildFinal('spanking_hardness'),
      how_bared: buildFinal('how_bared'),
      position: buildFinal('position'),
      implement: buildFinal('implement'),
      most_important: buildFinal('most_important'),
      aftercare: answers.aftercare || null,
      safe_word: answers.safe_word || null,
      interested_in_session: answers.interested_in_session || null,
      contact_email: answers.contact_email || null,
      contact_preference: contactPreferenceLabel,
    };
  }, [answers, others, step]);

  useEffect(() => {
    const sendAbandoned = () => {
      if (submittedRef.current) return;
      const payload = latestPayloadRef.current;
      if (!payload) return;
      // Only fire if at least one meaningful value was entered
      const hasData = Object.entries(payload).some(([k, v]) => {
        if (['session_id', 'furthest_step'].includes(k)) return false;
        return v !== null && v !== '' && v !== undefined;
      });
      if (!hasData) return;

      const url = `${API}/survey/abandoned`;
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, blob);
        } else {
          // Fallback: fire-and-forget fetch with keepalive
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {});
        }
      } catch (err) {
        // Non-fatal: abandonment beacon failed. Log locally only.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Abandoned survey beacon failed:', err);
        }
      }
    };

    const onBeforeUnload = () => sendAbandoned();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendAbandoned();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      // Fire on SPA navigation (component unmount)
      sendAbandoned();
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const current = STEPS[step];
  const value = answers[current.key];
  const otherText = others[current.key] || '';
  const isLast = step === STEPS.length - 1;

  const canProceed = () => {
    if (current.key === 'age') {
      const n = parseInt(value, 10);
      return !isNaN(n) && n >= 18 && n <= 120;
    }
    if (current.type === 'radio_other') {
      if (!value) return false;
      if (value === OTHER) return otherText.trim().length > 0;
      return true;
    }
    if (current.type === 'radio_conditional_email') {
      if (!value) return false;
      if (value === 'Yes') {
        const pref = answers.contact_pref;
        if (!pref) return false;
        if (pref === 'email') {
          const email = (answers.contact_email || '').trim();
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        }
        return pref === 'later';
      }
      return true;
    }
    return String(value || '').trim().length > 0;
  };

  const handleChange = (val) => setAnswers({ ...answers, [current.key]: val });

  const buildFinalAnswer = (key) => {
    const v = answers[key];
    if (v === OTHER) return `Other: ${(others[key] || '').trim()}`;
    return v;
  };

  const getValidationMessage = () => {
    if (current.key === 'age') return 'Enter a valid age (18 or older).';
    if (current.type === 'radio_other' && value === OTHER) return 'Please fill in the "Other" text.';
    if (current.type === 'radio_conditional_email' && value === 'Yes') {
      if (!answers.contact_pref) return 'Please choose an option below.';
      if (answers.contact_pref === 'email') return 'Please enter a valid email address.';
    }
    return 'This field cannot be empty.';
  };

  const goNext = async () => {
    if (!canProceed()) {
      toast({
        title: 'Please answer this question',
        description: getValidationMessage(),
      });
      return;
    }
    if (!isLast) {
      setStep(step + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    const isInterested = answers.interested_in_session === 'Yes';
    const wantsEmailContact = isInterested && answers.contact_pref === 'email';
    let submitContactPref = null;
    if (isInterested) {
      submitContactPref = wantsEmailContact ? 'Provided email' : 'Will reach out in the future';
    }
    const submitContactEmail = wantsEmailContact ? (answers.contact_email || '').trim() : null;

    try {
      await axios.post(`${API}/survey`, {
        age: parseInt(answers.age, 10),
        gender: answers.gender,
        city: answers.city.trim(),
        spanking_hardness: buildFinalAnswer('spanking_hardness'),
        how_bared: buildFinalAnswer('how_bared'),
        position: buildFinalAnswer('position'),
        implement: buildFinalAnswer('implement'),
        most_important: buildFinalAnswer('most_important'),
        aftercare: answers.aftercare,
        safe_word: answers.safe_word,
        interested_in_session: answers.interested_in_session,
        contact_email: submitContactEmail,
        contact_preference: submitContactPref,
      });
      setSubmitted(true);
      trackEvent('survey_complete', { interested_in_session: answers.interested_in_session || 'unknown' });
      toast({ title: 'Thank you!', description: 'Your survey response was recorded.' });
    } catch (e) {
      toast({
        title: 'Submission failed',
        description: e?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => step > 0 && setStep(step - 1);

  const submitLabel = (() => {
    if (submitting) return 'Submitting...';
    if (isLast) return 'Submit';
    return 'Next';
  })();

  if (submitted) {
    return (
      <div>
        <PageHeader
          eyebrow="Survey"
          title="Thank You"
          subtitle="Your response has been recorded."
          image="https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=3840"
        />
        <section className="bg-neutral-50">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center">
            <CheckCircle2 size={56} className="mx-auto text-neutral-900 mb-6" strokeWidth={1.5} />
            <h2 className="font-serif text-4xl text-neutral-900 mb-4">Response Recorded</h2>
            <p className="text-neutral-600 mb-8">
              I appreciate you taking the time to complete the survey. Your answers help me better
              understand and serve my clients.
            </p>
            <Button
              onClick={() => {
                setAnswers(initialAnswers);
                setOthers(initialOthers);
                setStep(0);
                setSubmitted(false);
              }}
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-11 px-8 text-sm tracking-widest uppercase"
            >
              Take Again
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Confidential"
        title="Survey"
        subtitle="A few quick questions. All answers are anonymous."
        image="https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=3840"
      />
      <section className="bg-neutral-50">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          {/* Progress bar */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-neutral-500">
                Question {step + 1} of {STEPS.length}
              </span>
              <span className="text-xs uppercase tracking-widest text-neutral-500">
                {Math.round(((step + 1) / STEPS.length) * 100)}%
              </span>
            </div>
            <div className="h-1 bg-neutral-200 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-neutral-900 transition-all duration-500"
                style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-8 md:p-12">
            <h3 className="font-serif text-2xl md:text-3xl text-neutral-900 font-bold mb-8 leading-snug">
              {current.label}
            </h3>

            {current.type === 'number' && (
              <Input
                type="number"
                min={18}
                max={120}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && goNext()}
                placeholder="Enter your age"
                className="h-14 text-2xl rounded-none border-neutral-300 focus-visible:ring-neutral-900 max-w-xs"
                autoFocus
              />
            )}

            {current.type === 'text' && (
              <Input
                type="text"
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && goNext()}
                placeholder="Enter your city"
                className="h-14 text-xl rounded-none border-neutral-300 focus-visible:ring-neutral-900"
                autoFocus
              />
            )}

            {current.type === 'radio' && (
              <RadioGroup value={value} onValueChange={handleChange} className="space-y-3">
                {current.options.map((opt) => (
                  <label
                    key={opt}
                    htmlFor={`opt-${opt}`}
                    className={`flex items-center gap-4 p-5 border cursor-pointer transition-colors ${
                      value === opt
                        ? 'border-neutral-900 bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <RadioGroupItem value={opt} id={`opt-${opt}`} />
                    <span className="text-lg text-neutral-900">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            )}

            {current.type === 'radio_other' && (
              <>
                <RadioGroup value={value} onValueChange={handleChange} className="space-y-3">
                  {current.options.map((opt) => (
                    <label
                      key={opt}
                      htmlFor={`opt-${current.key}-${opt}`}
                      className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${
                        value === opt
                          ? 'border-neutral-900 bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      <RadioGroupItem value={opt} id={`opt-${current.key}-${opt}`} className="mt-1" />
                      <span className="text-base text-neutral-900 leading-relaxed">{opt}</span>
                    </label>
                  ))}
                  <label
                    htmlFor={`opt-${current.key}-other`}
                    className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${
                      value === OTHER
                        ? 'border-neutral-900 bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <RadioGroupItem value={OTHER} id={`opt-${current.key}-other`} className="mt-1" />
                    <span className="text-base text-neutral-900 leading-relaxed">Other (fill in the blank)</span>
                  </label>
                </RadioGroup>
                {value === OTHER && (
                  <Textarea
                    value={otherText}
                    onChange={(e) => setOthers({ ...others, [current.key]: e.target.value })}
                    placeholder="Please describe..."
                    rows={3}
                    className="mt-4 rounded-none border-neutral-300 focus-visible:ring-neutral-900 resize-none"
                    autoFocus
                  />
                )}
              </>
            )}

            {current.type === 'radio_conditional_email' && (
              <>
                <RadioGroup value={value} onValueChange={handleChange} className="space-y-3">
                  {current.options.map((opt) => (
                    <label
                      key={opt}
                      htmlFor={`opt-${current.key}-${opt}`}
                      className={`flex items-center gap-4 p-5 border cursor-pointer transition-colors ${
                        value === opt
                          ? 'border-neutral-900 bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      <RadioGroupItem value={opt} id={`opt-${current.key}-${opt}`} />
                      <span className="text-lg text-neutral-900">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
                {value === 'Yes' && (
                  <div className="mt-6 animate-fadein space-y-4">
                    <p className="text-xs uppercase tracking-widest text-neutral-700">How would you like to proceed?</p>
                    <RadioGroup
                      value={answers.contact_pref}
                      onValueChange={(v) => setAnswers({ ...answers, contact_pref: v })}
                      className="space-y-3"
                    >
                      <label
                        htmlFor="pref-email"
                        className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${
                          answers.contact_pref === 'email'
                            ? 'border-neutral-900 bg-neutral-50'
                            : 'border-neutral-200 hover:border-neutral-400'
                        }`}
                      >
                        <RadioGroupItem value="email" id="pref-email" className="mt-1" />
                        <span className="text-base text-neutral-900">Contact me at my email</span>
                      </label>
                      <label
                        htmlFor="pref-later"
                        className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${
                          answers.contact_pref === 'later'
                            ? 'border-neutral-900 bg-neutral-50'
                            : 'border-neutral-200 hover:border-neutral-400'
                        }`}
                      >
                        <RadioGroupItem value="later" id="pref-later" className="mt-1" />
                        <span className="text-base text-neutral-900">I'll reach out in the future</span>
                      </label>
                    </RadioGroup>

                    {answers.contact_pref === 'email' && (
                      <div className="animate-fadein pt-2">
                        <label htmlFor="contact-email" className="block text-xs uppercase tracking-widest text-neutral-700 mb-2">
                          Your email address
                        </label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={answers.contact_email}
                          onChange={(e) => setAnswers({ ...answers, contact_email: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && goNext()}
                          placeholder="you@example.com"
                          className="h-12 text-base rounded-none border-neutral-300 focus-visible:ring-neutral-900"
                          autoFocus
                        />
                        <p className="text-xs text-neutral-500 mt-2">
                          Only used to contact you privately about a session. Kept confidential.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="mt-10 flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={goBack}
                disabled={step === 0}
                className="rounded-none border-neutral-300 h-11 px-6 text-sm tracking-widest uppercase disabled:opacity-40"
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              <Button
                onClick={goNext}
                disabled={submitting}
                className="rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-11 px-8 text-sm tracking-widest uppercase"
              >
                {submitLabel}{' '}
                {!submitting && <ArrowRight size={16} className="ml-2" />}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Survey;
